/**
 * CompanyPicker - Reusable modal for searching and selecting companies
 * 
 * A standalone component that displays a modal with a search input and DataGrid
 * for searching and selecting companies with a Promise-based API.
 * 
 * Features:
 * - Text search with debouncing
 * - DataGrid with server-side pagination
 * - Exclude specific company IDs from results
 * 
 * Usage:
 * ```typescript
 * import { CompanyPicker } from '@/components/CompanyPicker';
 * 
 * // Simple usage
 * const result = await CompanyPicker.show();
 * if (result.selected) {
 *   console.log('Selected:', result.companyId, result.companyName);
 * }
 * 
 * // With options
 * const result = await CompanyPicker.show({
 *   title: 'Select Company',
 *   excludeCompanyId: 123, // Exclude current company
 * });
 * ```
 */

import { DataGrid, type DataGridColumn } from '@/components/DataGrid';
import { ModalSystem } from '@/services/ModalSystem';
import { ToasterService } from '@/services/ToasterService';
import { Logger } from '@/services/Logger';

const logger = Logger.scope('CompanyPicker');

// ============================================================================
// Types
// ============================================================================

export interface CompanyPickerOptions {
  /** Modal title (default: 'Select Company') */
  title?: string;
  
  /** Company ID to exclude from results (e.g., current company being edited) */
  excludeCompanyId?: number;
  
  /** Additional company IDs to exclude */
  excludeCompanyIds?: number[];
  
  /** Custom page size for results (default: 10) */
  pageSize?: number;
  
  /** Modal width class (default: 'w-[800px] max-w-[90vw]') */
  modalWidth?: string;
  
  /** API service to use for searching companies (injectable for tenant-specific APIs) */
  apiService?: CompanyPickerApiService;
}

export interface CompanyPickerResult {
  /** Whether a company was selected (true) or cancelled (false) */
  selected: boolean;
  
  /** Selected company ID (if selected) */
  companyId?: number;
  
  /** Selected company name (if selected) */
  companyName?: string;
  
  /** Full record data (if selected) */
  record?: Record<string, unknown>;
}

/**
 * Interface for the API service that CompanyPicker uses
 * This allows different tenants to provide their own search implementation
 */
export interface CompanyPickerApiService {
  /** Search companies with pagination and filtering */
  searchCompanies(params: {
    PageNumber: number;
    RecordsPerPage: number;
    Filtering?: string[];
    SortColumnName?: string | null;
    SortAscending?: boolean;
  }): Promise<{
    Companies: CompanySummary[];
    TotalRecordCount: number;
  }>;
}

/** Minimal company summary for the picker */
export interface CompanySummary {
  CompanyId: number;
  CompanyName: string;
  Industry?: string | null;
  City?: string | null;
  MainTelNo?: string | null;
}

// ============================================================================
// CompanyPicker Component
// ============================================================================

/**
 * CompanyPicker - Static modal for searching and selecting companies
 */
export class CompanyPicker {
  private options: Required<Omit<CompanyPickerOptions, 'excludeCompanyId' | 'excludeCompanyIds' | 'apiService'>> & 
    Pick<CompanyPickerOptions, 'excludeCompanyId' | 'excludeCompanyIds' | 'apiService'>;
  
  private modalId: string | null = null;
  private dataGrid: DataGrid | null = null;
  private currentPage: number = 1;
  private pageSize: number;
  private searchTerm: string = '';
  private sortColumn: string | null = null;
  private sortDirection: 'asc' | 'desc' = 'asc';
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  
  private resolvePromise: ((result: CompanyPickerResult) => void) | null = null;
  
  private constructor(options: CompanyPickerOptions = {}) {
    this.options = {
      title: options.title ?? 'Select Company',
      pageSize: options.pageSize ?? 10,
      modalWidth: options.modalWidth ?? 'w-[800px] max-w-[90vw]',
      excludeCompanyId: options.excludeCompanyId,
      excludeCompanyIds: options.excludeCompanyIds,
      apiService: options.apiService
    };
    this.pageSize = this.options.pageSize;
  }
  
  /**
   * Show the company picker modal and return a Promise with the result
   * 
   * @param options - Configuration options
   * @returns Promise resolving to the picker result
   * 
   * @example
   * ```typescript
   * const result = await CompanyPicker.show({
   *   excludeCompanyId: currentCompanyId
   * });
   * 
   * if (result.selected) {
   *   console.log('Selected company:', result.companyId, result.companyName);
   * }
   * ```
   */
  static show(options: CompanyPickerOptions = {}): Promise<CompanyPickerResult> {
    const picker = new CompanyPicker(options);
    return picker.showModal();
  }
  
  private showModal(): Promise<CompanyPickerResult> {
    return new Promise((resolve) => {
      this.resolvePromise = resolve;
      
      // Reset state
      this.currentPage = 1;
      this.searchTerm = '';
      this.sortColumn = null;
      this.sortDirection = 'asc';
      
      const modalHtml = this.buildModalHtml();
      const modalTitle = this.buildModalTitle();
      
      this.modalId = ModalSystem.show({
        title: modalTitle,
        html: modalHtml,
        width: this.options.modalWidth,
        buttons: [
          {
            caption: 'Cancel',
            className: 'btn-ghost',
            callback: () => {
              this.handleCancel();
              return true;
            }
          }
        ],
        onOpened: () => this.initializeComponents(),
        onClosed: () => this.cleanup()
      });
    });
  }
  
  private buildModalHtml(): string {
    return `
      <div class="flex flex-col gap-4" style="height: 60vh; min-height: 400px;">
        <!-- Search Input -->
        <div class="flex items-center gap-2">
          <div class="relative flex-1">
            <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-sm z-10 pointer-events-none"></i>
            <input 
              type="text" 
              id="company-picker-search-input" 
              class="input input-bordered w-full pl-9" 
              placeholder="Type company name to search..."
              autocomplete="off"
            />
          </div>
          <button id="company-picker-search-clear" class="btn btn-ghost btn-sm" title="Clear search">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <!-- Results Grid -->
        <div id="company-picker-grid-container" class="flex-1 overflow-hidden"></div>
      </div>
    `;
  }
  
  private buildModalTitle(): string {
    return `<i class="fas fa-building text-primary mr-2"></i>${this.options.title}`;
  }
  
  private async initializeComponents(): Promise<void> {
    try {
      await this.initializeGrid();
      this.initializeSearchInput();
      await this.loadResults();
    } catch (error) {
      logger.error('Failed to initialize CompanyPicker', { error });
      ToasterService.error('Failed to initialize company picker');
    }
  }
  
  private async initializeGrid(): Promise<void> {
    const container = document.getElementById('company-picker-grid-container');
    if (!container) {
      logger.error('Company picker grid container not found');
      return;
    }
    
    const columns: DataGridColumn[] = [
      { 
        field: 'CompanyName', 
        header: 'Company Name', 
        sortable: true,
        formatter: (value: string | null, row: Record<string, unknown>) => {
          return `<span class="font-semibold text-primary cursor-pointer hover:underline flex items-center gap-2"><i class="fas fa-building"></i> ${value || '-'}</span>`;
        }
      },
      { 
        field: 'Industry', 
        header: 'Industry', 
        width: '150px',
        sortable: true,
        formatter: (value: string | null) => value || '<span class="text-base-content/50">-</span>'
      },
      { 
        field: 'City', 
        header: 'City', 
        width: '120px',
        sortable: true,
        formatter: (value: string | null) => value || '<span class="text-base-content/50">-</span>'
      },
      { 
        field: 'MainTelNo', 
        header: 'Phone', 
        width: '130px',
        sortable: false,
        formatter: (value: string | null) => value || '<span class="text-base-content/50">-</span>'
      }
    ];
    
    this.dataGrid = new DataGrid(container, {
      columns,
      localData: [],
      pageSize: this.pageSize,
      selectable: false,
      sortable: true,
      searchable: false,
      mode: 'server',
      totalRecords: 0,
      onSort: (column, direction) => this.handleSort(column, direction),
      onPageChange: (page, pageSize) => this.handlePageChange(page, pageSize),
      onRowClick: (row) => this.handleRowClick(row as CompanySummary)
    });
    
    await this.dataGrid.initialize();
  }
  
  private initializeSearchInput(): void {
    const searchInput = document.getElementById('company-picker-search-input') as HTMLInputElement;
    const clearBtn = document.getElementById('company-picker-search-clear');
    
    if (searchInput) {
      searchInput.addEventListener('input', () => this.handleSearchInput(searchInput.value));
      searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
          }
          this.loadResults();
        }
      });
      searchInput.focus();
    }
    
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        if (searchInput) {
          searchInput.value = '';
          this.searchTerm = '';
          this.currentPage = 1;
          this.loadResults();
        }
      });
    }
  }
  
  private handleSearchInput(value: string): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    this.debounceTimer = setTimeout(() => {
      this.searchTerm = value;
      this.currentPage = 1;
      this.loadResults();
    }, 300);
  }
  
  private handleSort(column: string, direction: 'asc' | 'desc'): void {
    this.sortColumn = column;
    this.sortDirection = direction;
    this.currentPage = 1;
    this.loadResults();
  }
  
  private handlePageChange(page: number, pageSize: number): void {
    this.currentPage = page;
    this.pageSize = pageSize;
    this.loadResults();
  }
  
  private async loadResults(): Promise<void> {
    if (!this.dataGrid || !this.options.apiService) {
      logger.error('DataGrid or API service not initialized');
      return;
    }
    
    try {
      this.dataGrid.showLoading();
      
      // Build filtering
      const filtering: string[] = [];
      if (this.searchTerm && this.searchTerm.trim()) {
        filtering.push(`Company_Name like '${this.searchTerm.trim()}%'`);
      }
      
      // Map display column names to API field names
      let sortColumnName: string | null = null;
      if (this.sortColumn) {
        const fieldMap: Record<string, string> = {
          'CompanyName': 'Company_Name',
          'Industry': 'Company_Industry',
          'City': 'Company_City',
          'MainTelNo': 'Company_MainTelNo'
        };
        sortColumnName = fieldMap[this.sortColumn] || this.sortColumn;
      }
      
      const response = await this.options.apiService.searchCompanies({
        PageNumber: this.currentPage,
        RecordsPerPage: this.pageSize,
        Filtering: filtering.length > 0 ? filtering : undefined,
        SortColumnName: sortColumnName,
        SortAscending: this.sortDirection !== 'desc'
      });
      
      // Filter out excluded companies
      let companies = response.Companies || [];
      const excludeIds = new Set<number>();
      if (this.options.excludeCompanyId) {
        excludeIds.add(this.options.excludeCompanyId);
      }
      if (this.options.excludeCompanyIds) {
        this.options.excludeCompanyIds.forEach(id => excludeIds.add(id));
      }
      if (excludeIds.size > 0) {
        companies = companies.filter(c => !excludeIds.has(c.CompanyId));
      }
      
      this.dataGrid.setData({
        records: companies,
        total: response.TotalRecordCount,
        page: this.currentPage
      });
      this.dataGrid.hideLoading();
      
      logger.debug('Company search results loaded', { 
        count: companies.length, 
        total: response.TotalRecordCount 
      });
      
    } catch (error) {
      logger.error('Failed to load company search results', { error });
      ToasterService.error('Failed to load companies');
      if (this.dataGrid) {
        this.dataGrid.setData({ records: [], total: 0 });
        this.dataGrid.hideLoading();
      }
    }
  }
  
  private handleRowClick(company: CompanySummary): void {
    logger.info('Company selected', { 
      companyId: company.CompanyId, 
      companyName: company.CompanyName 
    });
    
    // IMPORTANT: Store resolve function before hiding modal
    // because cleanup() (called by onClosed) sets this.resolvePromise = null
    const resolve = this.resolvePromise;
    
    // Close modal
    if (this.modalId) {
      ModalSystem.hide(this.modalId);
    }
    
    // Return result AFTER storing the resolve reference
    resolve?.({
      selected: true,
      companyId: company.CompanyId,
      companyName: company.CompanyName,
      record: company as unknown as Record<string, unknown>
    });
  }
  
  private handleCancel(): void {
    logger.debug('Company picker cancelled');
    this.resolvePromise?.({ selected: false });
  }
  
  private cleanup(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    
    if (this.dataGrid) {
      this.dataGrid.destroy();
      this.dataGrid = null;
    }
    
    this.modalId = null;
    this.resolvePromise = null;
  }
}
